import { ConfigService } from '@axo/config';
import { sitePathDefaultsConfig } from '@axo/config/registry/presets/site-path-defaults.config';

/**
 * Internal domains that are reverse proxied (site-one)
 */
const internalDomains = [
  'localhost:3030', // site-one dev
  'axo.ngrok.dev',
  'one.axo-test.io',
  'ax0-group.vercel.app',
  'one.axogroup.com',
];

const stagingDomains = [
  'site-fi.axo-test.io',
  'site-no.axo-test.io',
  'site-se.axo-test.io',
  'test.{{domain}}',
];

type Args = {
  product:
    | 'unsecured-loan'
    | 'my-page'
    | 'secured-loan'
    | 'secured-loan-my-page'
    | 'discovery-loan'
    | 'discovery-loan-suggestions'
    | 'insurance';
  /* @deprecated – temporary solution for mypage.axofinans... subdomain → axofinans... root domain redirects */
  isAbsoluteRedirect?: boolean;
};

/**
 * create redirect to product pages – relative or absolute, default or localised
 *
 * supports `@axo/site-one` and `@axo/white-label-host` (brand-aware embeds)
 *
 * 1. white label : absolute redirect to brand domain with localised slug
 * 2. brand domain : relative redirect to localised slug
 * 3. internal domain : relative redirect to default slug – site-one (bare / no reverse proxy)
 * 4. staging (on brand) domain : relative redirect to localised slug
 */

export const createRedirectTo = ({
  product,
  isAbsoluteRedirect,
}: Args): string => {
  const { domain: brandDomain, path: localisedPath } =
    ConfigService.get('site');
  const defaultPath = sitePathDefaultsConfig.path;

  const currentDomain =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : undefined;

  const isInternalDomain =
    !!currentDomain && internalDomains.some((d) => currentDomain.includes(d));
  const isStagingDomain =
    !!currentDomain &&
    stagingDomains.some((d) =>
      currentDomain.includes(d.replace('{{domain}}', brandDomain))
    );
  const isBrandDomain =
    !!currentDomain &&
    currentDomain.endsWith(brandDomain) &&
    !currentDomain.includes('test.');

  const baseDomain = isInternalDomain
    ? currentDomain
    : `https://${brandDomain}`;

  const path = isInternalDomain ? defaultPath : localisedPath;
  /** also add the defaults (site-one raw) paths to sitePathDefaultsConfig `@axo/config/registry/presets/site-path-defaults.config` */
  // FIXME should mapping be abstracted ? or use raw paths from site-one – f.ex. `'/secured-loans/apply': path.securedLoansApply`
  const productPaths: Record<typeof product, string | undefined> = {
    'unsecured-loan': path.loansApply,
    'my-page': path.mypage,
    'secured-loan': path.securedLoansApply,
    'secured-loan-my-page': path.securedLoansMypage,
    'discovery-loan': path.discoveryLoansApply,
    'discovery-loan-suggestions': path.discoveryLoansSuggestions,
    insurance: path.insurance,
  };

  const selectedPath = productPaths[product];
  if (!selectedPath) {
    // FIXME: throw new Error(`Unsupported product: ${product}`);
    console.error(`Unsupported product: ${product}`);
    return '/';
  }
  const isSelectedPathAbsoluteUrl = /^(http|https):\/\//.test(selectedPath);

  // For internal domains, always return relative URLs regardless of isAbsoluteRedirect
  // For other domains, respect isAbsoluteRedirect flag
  const isRelativeUrl =
    isInternalDomain ||
    (!isAbsoluteRedirect && (isStagingDomain || isBrandDomain));

  return isRelativeUrl || isSelectedPathAbsoluteUrl
    ? selectedPath
    : new URL(selectedPath, baseDomain).toString();
};
